/**
 * Houseplant v2 — Hero banner slider
 *
 * Drives sections/hp-hero-banner.liquid. The track is a native scroll-snap
 * container, so swipe, momentum and snapping are the browser's job; this only
 * owns three things:
 *
 *   1. Pagination bars — click to go to a slide, and stay in sync when the
 *      visitor swipes.
 *   2. Auto-rotation, with the pauses WCAG 2.2.2 expects: hover, keyboard focus,
 *      a hidden tab, and a permanent stop the moment the visitor picks a slide
 *      themselves. Skipped entirely under prefers-reduced-motion.
 *   3. Theme-editor plumbing, so selecting a slide block scrolls it into view.
 *
 * Deliberately not built on the base <slider-component>: it early-returns
 * without prev/next buttons and models "pages" of N visible items, neither of
 * which fits a one-slide-per-view hero with bar pagination.
 */
class HpHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hp-hero__slides');
    this.slides = Array.from(this.querySelectorAll('.hp-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.hp-hero__dot'));

    if (!this.track || this.slides.length < 2) return;

    this.index = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = this.onScroll.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        // An explicit choice ends auto-rotation for the rest of the visit —
        // taking control back off the visitor a few seconds later is hostile.
        this.stopAutoplay(true);
        this.goTo(Number(dot.dataset.index));
      });
    });

    /* Desktop prev/next. Same contract as the dots: an explicit choice ends
       auto-rotation for the rest of the visit. They are hidden below 990 in CSS
       rather than omitted here, because the track is a native swipe scroller on
       touch and the markup is one document at every width. */
    this.querySelectorAll('[data-hero-step]').forEach((button) => {
      button.addEventListener('click', () => {
        this.stopAutoplay(true);
        if (Number(button.dataset.heroStep) < 0) this.prev();
        else this.next();
      });
    });

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Any direct interaction with the track (swipe, drag, keyboard scroll)
    // counts as taking control, same as clicking a bar.
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
      this.track.addEventListener(type, () => this.stopAutoplay(true), { passive: true, once: true });
    });

    this.addEventListener('pointerenter', () => this.pauseAutoplay());
    this.addEventListener('pointerleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resumeAutoplay();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay(true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /* ---------------------------------------------------------------- moving */

  goTo(index, behavior) {
    const slide = this.slides[index];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: behavior || (this.reducedMotion.matches ? 'auto' : 'smooth'),
    });
    this.setActive(index);
  }

  /* Both wrap. With arrows always present, a dead button at either end reads as
     broken rather than as a boundary — and the track already loops on autoplay,
     so stopping at the ends would make the arrows disagree with it. */
  prev() {
    this.goTo((this.index - 1 + this.slides.length) % this.slides.length);
  }

  next() {
    this.goTo((this.index + 1) % this.slides.length);
  }

  setActive(index) {
    if (index === this.index) return;
    this.index = index;

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      // Removed rather than set to "false": aria-current="false" is still an
      // announced state in some screen readers.
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /**
   * Scroll position → active slide. Read inside rAF so a momentum scroll doesn't
   * queue a layout read per frame.
   */
  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;

      const origin = this.track.scrollLeft + this.track.offsetLeft;
      const nearest = this.slides.reduce(
        (best, slide, i) => {
          const distance = Math.abs(slide.offsetLeft - origin);
          return !best || distance < best.distance ? { i, distance } : best;
        },
        null
      );

      if (nearest) this.setActive(nearest.i);
    });
  }

  /* -------------------------------------------------------------- autoplay */

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.autoplayStopped) return;
    // `visibilitychange` only fires on a CHANGE — a page opened in a background
    // tab is already hidden at connect time and would otherwise rotate unseen,
    // landing the visitor on slide 3 when they finally look at it.
    if (document.hidden) return;
    this.pauseAutoplay();

    const seconds = parseInt(this.dataset.autoplaySpeed, 10) || 6;
    this.timer = setInterval(() => this.next(), seconds * 1000);
  }

  pauseAutoplay() {
    clearInterval(this.timer);
    this.timer = null;
  }

  /** @param {boolean} permanently — visitor took control; don't come back. */
  stopAutoplay(permanently) {
    this.pauseAutoplay();
    if (permanently) this.autoplayStopped = true;
  }

  resumeAutoplay() {
    if (document.hidden) return;
    this.startAutoplay();
  }

  onVisibilityChange() {
    if (document.hidden) this.pauseAutoplay();
    else this.resumeAutoplay();
  }
}

if (!customElements.get('hp-hero-slider')) {
  customElements.define('hp-hero-slider', HpHeroSlider);
}

/**
 * Theme editor: selecting a slide block in the sidebar should bring that slide
 * into view, and auto-rotation shouldn't fight the merchant while they edit.
 */
document.addEventListener('shopify:block:select', (event) => {
  const slider = event.target.closest?.('.hp-hero')?.querySelector('hp-hero-slider');
  if (!slider) return;

  slider.stopAutoplay(true);
  slider.goTo(Number(event.target.dataset.index) || 0);
});
